import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Settings, Save } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface FeatureFlags {
  advancedSearch: boolean;
  bulkOperations: boolean;
  mediaUpload: boolean;
  analytics: boolean;
  notifications: boolean;
  advancedFilters: boolean;
  exportFeatures: boolean;
  clientCommunication: boolean;
  mobileFeatures: boolean;
  integrations: boolean;
}

const defaultFeatures: FeatureFlags = {
  advancedSearch: false,
  bulkOperations: false,
  mediaUpload: false,
  analytics: false,
  notifications: false,
  advancedFilters: false,
  exportFeatures: false,
  clientCommunication: false,
  mobileFeatures: false,
  integrations: false,
};

interface AdminSettingsProps {
  onBack: () => void;
}

export const AdminSettings = ({ onBack }: AdminSettingsProps) => {
  const [features, setFeatures] = useState<FeatureFlags>(defaultFeatures);
  const { toast } = useToast();

  useEffect(() => {
    // Charger les paramètres depuis localStorage
    const savedFeatures = localStorage.getItem('adminFeatures');
    if (savedFeatures) {
      setFeatures({ ...defaultFeatures, ...JSON.parse(savedFeatures) });
    }
  }, []);

  const handleFeatureToggle = (feature: keyof FeatureFlags) => {
    setFeatures(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  const handleSave = () => {
    localStorage.setItem('adminFeatures', JSON.stringify(features));
    toast({
      title: "Paramètres sauvegardés",
      description: "Les fonctionnalités ont été mises à jour avec succès.",
    });
  };

  const featureCategories = [
    {
      title: "Recherche & Filtres",
      features: [
        { key: 'advancedSearch' as keyof FeatureFlags, label: 'Recherche avancée', description: 'Recherche multicritères avec filtres complexes' },
        { key: 'advancedFilters' as keyof FeatureFlags, label: 'Filtres avancés', description: 'Filtres par prix, année, équipements' },
      ]
    },
    {
      title: "Gestion des données",
      features: [
        { key: 'bulkOperations' as keyof FeatureFlags, label: 'Opérations en lot', description: 'Modification/suppression multiple d\'annonces' },
        { key: 'exportFeatures' as keyof FeatureFlags, label: 'Export de données', description: 'Export CSV/PDF des annonces' },
      ]
    },
    {
      title: "Médias & Contenu",
      features: [
        { key: 'mediaUpload' as keyof FeatureFlags, label: 'Upload multimédia', description: 'Upload d\'images et vidéos multiples' },
      ]
    },
    {
      title: "Analytics & Reporting",
      features: [
        { key: 'analytics' as keyof FeatureFlags, label: 'Tableaux de bord', description: 'Statistiques et analytics détaillées' },
      ]
    },
    {
      title: "Communication",
      features: [
        { key: 'notifications' as keyof FeatureFlags, label: 'Notifications', description: 'Alertes et notifications automatiques' },
        { key: 'clientCommunication' as keyof FeatureFlags, label: 'Communication client', description: 'Chat et système de messages' },
      ]
    },
    {
      title: "Intégrations",
      features: [
        { key: 'mobileFeatures' as keyof FeatureFlags, label: 'Fonctionnalités mobiles', description: 'Optimisations et features mobiles' },
        { key: 'integrations' as keyof FeatureFlags, label: 'Intégrations externes', description: 'API et intégrations tierces' },
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="border-0 bg-gradient-to-r from-slate-600 to-slate-700 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Settings className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Paramètres administrateur</CardTitle>
                <p className="text-slate-100 mt-1">
                  Activez ou désactivez les fonctionnalités avancées
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={handleSave}
                className="bg-white text-slate-600 hover:bg-slate-50 font-medium"
                size="lg"
              >
                <Save className="h-5 w-5 mr-2" />
                Sauvegarder
              </Button>
              <Button 
                onClick={onBack}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                size="lg"
              >
                Retour
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Paramètres par catégorie */}
      <div className="grid gap-6">
        {featureCategories.map((category) => (
          <Card key={category.title}>
            <CardHeader>
              <CardTitle className="text-lg">{category.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {category.features.map((feature) => (
                <div key={feature.key} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor={feature.key} className="text-base font-medium">
                      {feature.label}
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      {feature.description}
                    </p>
                  </div>
                  <Switch
                    id={feature.key}
                    checked={features[feature.key]}
                    onCheckedChange={() => handleFeatureToggle(feature.key)}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Résumé des fonctionnalités actives */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Résumé</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {Object.values(features).filter(Boolean).length}
              </div>
              <div className="text-sm text-green-700">Fonctionnalités actives</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-600">
                {Object.values(features).filter(f => !f).length}
              </div>
              <div className="text-sm text-gray-700">Fonctionnalités désactivées</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};